import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import './CustomAlert.css';
import './Confirm.css';
import alertLogo from '../../assets/alert-logo.png';

const AlertContext = createContext();

export const useAlert = () => {
  return useContext(AlertContext);
};

export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);
  const [confirmConfig, setConfirmConfig] = useState(null);

  const showConfirm = useCallback((message) => {
    return new Promise((resolve) => {
      setConfirmConfig({
        message,
        onConfirm: () => {
          setConfirmConfig(null);
          resolve(true);
        },
        onCancel: () => {
          setConfirmConfig(null);
          resolve(false);
        }
      });
    });
  }, []);

  const closeAlert = useCallback((id) => {
    setAlerts(prev => prev.map(alert => 
      alert.id === id ? { ...alert, isExiting: true } : alert
    ));

    // Remove completely after animation finishes
    setTimeout(() => {
      setAlerts(prev => prev.filter(alert => alert.id !== id));
    }, 400); // Matches CSS exit animation duration
  }, []);

  const showAlert = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now() + Math.random().toString(36).substring(7);
    setAlerts(prev => [...prev, { id, message, type, duration, isExiting: false }]);

    setTimeout(() => {
      closeAlert(id);
    }, duration);
  }, [closeAlert]);

  // Globally override window.alert to automatically use the custom alert everywhere
  useEffect(() => {
    const originalAlert = window.alert;
    window.alert = (message) => {
      showAlert(message, 'warning'); // Default to warning style for standard alerts
    };

    return () => {
      window.alert = originalAlert;
    };
  }, [showAlert]);

  return (
    <AlertContext.Provider value={{ showAlert, showConfirm }}>
      {children}
      
      {/* Confirm Dialog Overlay */}
      {confirmConfig && (
        <div className="custom-confirm-overlay">
          <div className="custom-confirm-dialog">
            <img src={alertLogo} alt="Alert" className="confirm-icon-image" />
            <p className="confirm-message">{confirmConfig.message}</p>
            <div className="confirm-actions">
              <button className="confirm-btn confirm-cancel" onClick={confirmConfig.onCancel}>Cancel</button>
              <button className="confirm-btn confirm-ok" onClick={confirmConfig.onConfirm}>OK</button>
            </div>
          </div>
        </div>
      )}

      <div className="custom-alerts-container">
        {alerts.map(alert => (
          <div 
            key={alert.id} 
            className={`custom-alert custom-alert-${alert.type} ${alert.isExiting ? 'alert-exit' : 'alert-enter'}`}
          >
            <div className="alert-content">
              <img src={alertLogo} alt="Alert" className="alert-icon-image" />
              <p className="alert-message">{alert.message}</p>
            </div>
            <button className="alert-close" onClick={() => closeAlert(alert.id)}>×</button>
            <div
              className={`alert-progress alert-progress-${alert.type}`}
              style={{ animationDuration: `${alert.duration || 4000}ms` }}
            ></div>
          </div>
        ))}
      </div>
    </AlertContext.Provider>
  );
};